import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  Image,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  SimpleGrid,
  useToast,
  Textarea,
  Switch,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
} from "react-icons/hi";
import Editor from "@monaco-editor/react";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelect from "./CodesetSelect";

const CodifySpecificationsEditor = ({ codifySpecification, isNew }) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [localSpecification, setLocalSpecification] = useState(codifySpecification || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (codifySpecification) {
      setLocalSpecification(codifySpecification);
    }
  }, [codifySpecification]);

  const handleChange = (field, value) => {
    setLocalSpecification((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = isNew ? "POST" : "PUT";
      const url = isNew
        ? `https://codify.poc.mrc.ai/api/specifications`
        : `https://codify.poc.mrc.ai/api/specifications/${localSpecification._id}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...localSpecification,
          updatedDate: new Date().toISOString(),
        }),
      });

      if (!res.ok) throw new Error("Failed to save codify specification");

      const saved = await res.json();
      setLocalSpecification(saved);

      toast({
        title: isNew ? "Specification created" : "Specification updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew) {
        navigate(`/codifyspecifications/${saved._id}`);
      }
    } catch (err) {
      console.error("Error saving codify specification:", err);
      toast({
        title: "Error saving specification",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate("/codify");
  };

  const handleValidateSchema = async () => {
    if (!localSpecification.schema || !localSpecification.schema.trim()) {
      toast({
        title: "No schema to validate",
        description: "Please enter a JSON Schema first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // First check if it's valid JSON
      const parsed = JSON.parse(localSpecification.schema);

      // Check if it has basic JSON Schema properties
      if (parsed.$schema || parsed.type || parsed.properties) {
        // Make a test validation call to the API to validate the schema structure
        const response = await fetch('https://codify.poc.mrc.ai/api/specifications/validate-schema', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: localSpecification.schema,
        });

        if (response.ok) {
          toast({
            title: "Schema is valid",
            description: "The JSON Schema is correctly formatted",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } else {
          const error = await response.json();
          toast({
            title: "Invalid JSON Schema",
            description: error.message || "Schema validation failed",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: "Valid JSON, but not a JSON Schema",
          description: "The JSON is valid but doesn't appear to be a JSON Schema (missing $schema, type, or properties)",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Invalid JSON",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handlePrettifySchema = () => {
    if (!localSpecification.schema || !localSpecification.schema.trim()) {
      toast({
        title: "No schema to format",
        description: "Please enter JSON first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const parsed = JSON.parse(localSpecification.schema);
      const formatted = JSON.stringify(parsed, null, 4);
      handleChange("schema", formatted);
      
      toast({
        title: "Schema formatted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Cannot format",
        description: "Invalid JSON: " + err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            mb={4}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <HStack spacing={4} mb={4}>
              <Tooltip label="Back to Codify">
                <IconButton
                  icon={<HiOutlineArrowNarrowLeft />}
                  aria-label="Back"
                  onClick={handleClose}
                />
              </Tooltip>
              <Heading size="lg">
                {isNew ? "New Codify Specification" : "Edit Codify Specification"}
              </Heading>
              <Box flex="1" />
              <Button
                leftIcon={<HiOutlineSave />}
                colorScheme="linkedin"
                onClick={handleSave}
                isLoading={saving}>
                Save
              </Button>
            </HStack>

            <Tabs colorScheme="linkedin">
              <TabList>
                <Tab>Details</Tab>
                <Tab>Schema</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={localSpecification.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Specification name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Input
                  value={localSpecification.status || ""}
                  onChange={(e) => handleChange("status", e.target.value)}
                  placeholder="e.g., draft, active, archived"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Collection</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY003"
                  value={
                    localSpecification.codifyCollection
                      ? { code: localSpecification.codifyCollection, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("codifyCollection", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Data Standard</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY001"
                  value={
                    localSpecification.dataStandard
                      ? { code: localSpecification.dataStandard, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("dataStandard", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Data Type</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY002"
                  value={
                    localSpecification.dataType
                      ? { code: localSpecification.dataType, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("dataType", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Model Provider</FormLabel>
                <Input
                  value={localSpecification.modelProvider || ""}
                  onChange={(e) => handleChange("modelProvider", e.target.value)}
                  placeholder="e.g., OpenAI, Anthropic"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Model</FormLabel>
                <CodesetSelect
                  codesetCode="CODIFY006"
                  value={
                    localSpecification.model
                      ? { code: localSpecification.model, description: "" }
                      : null
                  }
                  onChange={(selected) =>
                    handleChange("model", selected?.code || "")
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Organisation Owner ID</FormLabel>
                <Input
                  value={localSpecification.organisationOwnerId || ""}
                  onChange={(e) => handleChange("organisationOwnerId", e.target.value)}
                  placeholder="Organisation owner ID"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Is Default</FormLabel>
                <Switch
                  isChecked={localSpecification.isDefault || false}
                  onChange={(e) => handleChange("isDefault", e.target.checked)}
                />
              </FormControl>
            </SimpleGrid>

                  <FormControl mt={4}>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={localSpecification.description || ""}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Description of this specification"
                      rows={4}
                    />
                  </FormControl>

                  {!isNew && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                <FormControl>
                  <FormLabel>Created By</FormLabel>
                  <Input value={localSpecification.createdBy || ""} isReadOnly />
                </FormControl>

                <FormControl>
                  <FormLabel>Created Date</FormLabel>
                  <Input
                    value={
                      localSpecification.createdDate
                        ? new Date(localSpecification.createdDate).toLocaleString()
                        : ""
                    }
                    isReadOnly
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Updated By</FormLabel>
                  <Input value={localSpecification.updatedBy || ""} isReadOnly />
                </FormControl>

                <FormControl>
                  <FormLabel>Updated Date</FormLabel>
                  <Input
                    value={
                      localSpecification.updatedDate
                        ? new Date(localSpecification.updatedDate).toLocaleString()
                        : ""
                    }
                    isReadOnly
                  />
                </FormControl>
              </SimpleGrid>
                  )}
                </TabPanel>

                <TabPanel>
                  <FormControl height="calc(100vh - 300px)">
                    <HStack mb={2}>
                      <FormLabel mb={0}>JSON Schema</FormLabel>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={handleValidateSchema}
                      >
                        Validate Schema
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="teal"
                        variant="outline"
                        onClick={handlePrettifySchema}
                      >
                        Format JSON
                      </Button>
                    </HStack>
                    <Box 
                      border="1px solid" 
                      borderColor="gray.300" 
                      borderRadius="md" 
                      overflow="hidden"
                      height="calc(100% - 40px)"
                    >
                      <Editor
                        height="100%"
                        defaultLanguage="json"
                        value={localSpecification.schema || ""}
                        onChange={(value) => handleChange("schema", value || "")}
                        theme="vs-light"
                        options={{
                          minimap: { enabled: false },
                          fontSize: 13,
                          lineNumbers: "on",
                          scrollBeyondLastLine: false,
                          automaticLayout: true,
                          tabSize: 4,
                          stickyScroll: { enabled: false },
                          folding: true,
                          showFoldingControls: "always",
                          foldingStrategy: "indentation",
                          foldingHighlight: true,
                          guides: {
                            indentation: true,
                            highlightActiveIndentation: true,
                          },
                        }}
                      />
                    </Box>
                  </FormControl>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CodifySpecificationsEditor;
